<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $t('messages.dashboard'),
                        href: meta.links.dashboard_index,
                    },
                    {
                        name: $tc('messages.relatives', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <div class="sm:col-span-4">
                    <label class="block text-sm font-medium leading-5 text-gray-700">{{ $t('messages.search') }}</label>
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <input
                            v-model="form.name_or_email"
                            :placeholder="`${$t('messages.search')}: ${$t('messages.name')}, ${$t('messages.email')}`"
                            type="text"
                            class="pr-10 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm w-full"
                        />
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.status')" for="active" />
                        <FormSelect
                            id="active"
                            required
                            v-model="form.active"
                            :options="options.active"
                            :null-option="false"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.active" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div class="w-full flex space-x-4 justify-end">
            <ActionsPanel v-show="selectedUsers.length">
                <inertia-link
                    v-if="filters.active === 'active'"
                    method="delete"
                    as="button"
                    :href="meta.links.deactivateMultiple"
                    :data="{ users: selectedUsers }"
                    role="menuitem"
                    class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                        class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                    >
                        <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z"></path>
                        <path
                            fill-rule="evenodd"
                            d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"
                            clip-rule="evenodd"
                        ></path>
                    </svg>
                    {{ $t('messages.deactivate') }}
                </inertia-link>
                <inertia-link
                    v-if="filters.active === 'inactive'"
                    method="post"
                    as="button"
                    :href="meta.links.activateMultiple"
                    :data="{ users: selectedUsers }"
                    role="menuitem"
                    class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                        class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                    >
                        <path
                            d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"
                        ></path>
                    </svg>
                    {{ $t('messages.activate') }}
                </inertia-link>
            </ActionsPanel>
            <AppButton element="InertiaLink" :href="meta.links.create">
                {{ $t('messages.create') }}
            </AppButton>
        </div>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <SelectAllNoneButtons
                                        :max-count="relatives.data.length"
                                        :selected-count="selectedUsers.length"
                                        @select-all="selectAll()"
                                        @select-none="selectedUsers = []"
                                        class="inline-flex"
                                    />
                                    <span>{{ $t('messages.name') }}</span>
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.students', 2) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(relative, index) in relatives.data"
                                :key="relative.id"
                            >
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <div>
                                        <input
                                            type="checkbox"
                                            name="selected-users"
                                            v-model="selectedUsers"
                                            :value="relative.id"
                                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded mr-1"
                                        />
                                        <span>
                                    {{ relative.full_name }}
                                        </span>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div class="text-xs">
                                        <div v-for="student in relative.students" :key="student.id">
                                            {{ student.full_name }}
                                        </div>
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex justify-end space-x-1"
                                >
                                    <InertiaSquareLinkButton action="show" :href="relative.links.show" />
                                    <InertiaSquareLinkButton action="edit" :href="relative.links.edit" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="relatives" />
        </template>
    </PanelPage>
</template>

<script>
import AdminLayout from '@/Shared/AdminLayout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import AppButton from '@/components/AppButton.vue';
import OptionsPanel from '@/components/OptionsPanel.vue';
import ActionsPanel from '@/components/ActionsPanel.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormError from '@/components/Forms/FormError.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import SelectAllNoneButtons from '@/components/SelectAllNoneButtons.vue';

export default {
    components: {
        SelectAllNoneButtons,
        SubmitFormButton,
        PanelPage,
        InertiaPagination,
        InertiaSquareLinkButton,
        AppButton,
        OptionsPanel,
        ActionsPanel,
        FormLabel,
        FormSelect,
        FormError,
    },
    layout: AdminLayout,
    name: 'Index',
    props: ['meta', 'relatives', 'filters', 'options'],
    data() {
        return {
            form: this.$inertia.form({
                name_or_email: this.filters.name_or_email,
                active: this.filters.active,
            }),
            selectedUsers: [],
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
        selectAll() {
            this.selectedUsers = this.relatives.data.map((relative) => relative.id);
        },
    },
};
</script>
